import { OctagonAlert } from 'lucide-react'
import type { ComponentPropsWithRef, ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../Button/Button'
import { Eyebrow } from '../Eyebrow/Eyebrow'
import s from './Page.module.css'

/**
 * Gabarit de page — le cadre que chaque écran du prototype répète : colonne pleine
 * hauteur, barre de titre (`.topbar`), zone de contenu défilante. Extrait tel quel des
 * deux premiers écrans transcrits, où ces blocs étaient dupliqués à l'octet près.
 *
 * Cinq pièces, composées librement :
 *
 * ```tsx
 * <Page>
 *   <PageHeader eyebrow="…" title="…">{actions}</PageHeader>
 *   <PageAlert message={actionError} onDismiss={dismiss} />
 *   <PageToolbar>{filtres, qui restent à l'écran}</PageToolbar>
 *   <PageBody>{contenu défilant}</PageBody>
 * </Page>
 * ```
 *
 * **Une seule zone défile, et c'est `PageBody`.** Tout ce qui est posé à côté — barre de
 * titre, barre épinglée — reste sous la main. C'est la séparation que fait le prototype, et
 * elle n'est pas cosmétique : une barre de filtres ou d'onglets rangée dans la zone
 * défilante quitte l'écran au premier défilement, et l'agent doit remonter tout son
 * formulaire pour la retrouver.
 *
 * `PageHeader` porte LE `<h1>` de l'écran — celui que le test de routage vérifie sur
 * chaque route. Un écran qui l'utilise n'a pas à en déclarer d'autre.
 */
export function Page({ className, ...props }: ComponentPropsWithRef<'section'>) {
  return <section className={cn(s.page, className)} {...props} />
}

export interface PageHeaderProps {
  /**
   * Affordance de retour, à gauche du titre — typiquement un `IconButton` rendu en
   * lien du routeur de l'app. Un slot, pas une prop `href` : ce paquet ne connaît
   * aucun routeur.
   */
  back?: ReactNode
  /** Ligne de contexte au-dessus du titre (petites capitales du prototype). */
  eyebrow?: ReactNode
  title: ReactNode
  /** `mono` pour les titres-références (« Contrôle #36739 ») ; `display` sinon. */
  titleFont?: 'display' | 'mono'
  /**
   * Ligne de contexte **sous** le titre — l'adresse d'un contrôle dans l'en-tête de
   * l'éditeur (`.ctx-addr` du prototype terrain).
   *
   * ⚠️ Ce n'est pas un `eyebrow` déplacé : les deux disent des choses différentes et
   * cohabitent. L'eyebrow situe l'écran dans l'application (« Campagne · Pôle Erdre et
   * Loire ») ; le sous-titre identifie l'objet qu'on regarde (« 27 Rue de la Cadaine »).
   * Le premier se lit avant le titre, le second après.
   */
  subtitle?: ReactNode
  /**
   * Le filet de séparation en bas de la barre. `false` quand la barre est **suivie d'un
   * élément de la même surface** — la barre d'onglets de l'éditeur : deux filets empilés
   * à un pixel d'écart se voient, et le prototype n'en dessine qu'un pour tout le bloc.
   */
  divider?: boolean
  /** Les actions de l'écran, alignées à droite. */
  children?: ReactNode
}

export function PageHeader({
  back,
  eyebrow,
  title,
  titleFont = 'display',
  subtitle,
  divider = true,
  children,
}: PageHeaderProps) {
  return (
    <header className={cn(s.topbar, !divider && s.topbarFlush)}>
      {back}
      <div className={s.heading}>
        {eyebrow !== undefined && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className={cn(s.title, titleFont === 'mono' && s.titleMono)}>{title}</h1>
        {subtitle !== undefined && <span className={s.subtitle}>{subtitle}</span>}
      </div>
      <div className={s.spacer} />
      {children}
    </header>
  )
}

/**
 * Bande épinglée entre la barre de titre et le contenu : elle ne défile pas.
 *
 * Le prototype la rend **frère** de la zone défilante, jamais dedans — la barre de filtres
 * d'une liste (`.ct-toolbar`) comme l'en-tête de contexte d'un détail (`.ctx`, `flex: none`).
 *
 * Elle ne met rien en page : sa raison d'être est de rester à l'écran, et ce qu'on y pose
 * — une `FilterBar`, une barre d'onglets — apporte sa propre disposition.
 */
export function PageToolbar({ className, ...props }: ComponentPropsWithRef<'div'>) {
  return <div className={cn(s.toolbar, className)} {...props} />
}

export interface PageAlertProps {
  /** Le refus du serveur mot pour mot, ou l'échec d'une lecture. */
  message: string | null | undefined
  /** Absent, « Masquer » ne se monte pas : le message tient jusqu'au geste suivant. */
  onDismiss?: () => void
}

/**
 * Le refus d'un geste de l'écran, ou l'échec d'une lecture qui laisse l'écran utilisable (un écran
 * laissé sans rien à montrer prend un état vide). ⚠️ Un refus **dans une boîte** se dit dans la
 * boîte (`Modal`, `error`) : ici, derrière le voile, il ne se lirait pas.
 */
export function PageAlert({ message, onDismiss }: PageAlertProps) {
  if (message === null || message === undefined) return null

  return (
    <p className={s.alert} role="alert">
      <OctagonAlert size={18} aria-hidden />
      <span className={s.alertText}>{message}</span>
      {onDismiss !== undefined && (
        <Button tone="ghost" size="sm" onClick={() => onDismiss()}>
          Masquer
        </Button>
      )}
    </p>
  )
}

/** Zone de contenu de la page : elle défile, tout le reste de la page tient en place. */
export function PageBody({ className, ...props }: ComponentPropsWithRef<'div'>) {
  return <div className={cn(s.body, className)} {...props} />
}
